import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { loadPageData } from "redux/actions/Request";

import { makeStyles } from "@material-ui/core/styles";

import {
  Chip,
  List,
  Fade,
  Dialog,
  Button,
  ListItem,
  TextField,
  DialogTitle,
  ListItemText,
  DialogContent,
  CircularProgress,
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import EditIcon from "@material-ui/icons/Edit";
import PublicIcon from "@material-ui/icons/Public";
import DeleteIcon from "@material-ui/icons/Delete";

import { isGlobal, isMicrosoft, getSecondaryDateText } from "./helpers";

import SearchField from "./SearchField";

import Request from "lib/api/Request";

const useStyles = makeStyles((theme) => ({
  formControl: {
    height: 80,
    padding: 0,
    marginRight: 5,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  dFlex: {
    display: "flex",
    alignItems: "flex-start",
  },
  formGrid: {
    height: 400,
    overflowY: "auto",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  filterPaper: {
    padding: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    height: "32px", // Hack fixed height until i can figure out a better way of handling this
    width: "100%",
  },
  searchFieldChip: {
    maxWidth: "80vw",
    marginRight: theme.spacing(0.5),
  },
  expandButton: {
    marginLeft: "auto",
  },
  expand: {
    transform: "rotate(0deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: "rotate(180deg)",
  },
  cardAction: {
    marginRight: theme.spacing(1),
  },
  savedQueries: {
    padding: theme.spacing(1),
  },
  savedQueriesSearch: {
    justifyContent: "flex-end",
    display: "flex",
  },
  savedQueriesList: {
    overflowY: "auto",
  },
  toolBar: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  titleBar: {
    marginRight: theme.spacing(2),
  },
  dataSource: {
    "& input": {
      height: 28,
    },
  },
  modelNameContainer: {
    display: "flex",
    alignItems: "center",
  },
  editIcon: {
    marginRight: theme.shape.padding,
  },
  closeIcon: {
    marginRight: 5,
    color: theme.palette.error.main,
  },
  checkIcon: {
    color: theme.palette.success.main,
  },
  confirmContainer: {
    minWidth: 320,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    "& p": {
      margin: 0,
      color: theme.palette.text.secondary,
    },
  },
  confirmDelete: {
    color: theme.palette.error.main,
  },
  savedReportBtnContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const SavedQueryModal = (props) => {
  const [savedQueries, setSavedQueries] = useState([]);
  const [modelToDelete, setModelToDelete] = useState(false);
  const [savedQueryFilter, setSavedQueryFilter] = useState([]);
  const [editingModelName, setEditingModelName] = useState("");
  const [isDeletingQuery, setIsDeletingQuery] = useState(false);
  const [reportModelToEdit, setReportModelToEdit] = useState({});
  const [warningText, setWarningText] = useState("Are you sure?");
  const [isLoadingScheduledReportsId, setIsLoadingScheduledReportsId] =
    useState(false);

  const classes = useStyles();

  useEffect(() => {
    const filter_ranked = props.queries.map((q) => {
      return {
        model: q,
        match: savedQueryFilter.filter((s) => q.name.toLowerCase().includes(s))
          .length,
      };
    });
    filter_ranked.sort((a, b) => (a.match > b.match ? -1 : 1));

    // set query list with only matched items
    if (savedQueryFilter.length)
      setSavedQueries(filter_ranked.filter((sq) => sq.match > 0));
    else setSavedQueries(filter_ranked);

    // when queries are reloaded
    // ensure delete UI is reset
    setIsDeletingQuery(false);
  }, [props.queries, savedQueryFilter]);

  const filterReports = (queries) => {
    if (props.license.isTagged("limited-to-microsoft")) {
      return queries
        .filter((o) => isGlobal(o))
        .filter((o) => isMicrosoft(o.model));
    }
    return queries;
  };

  const onClick = (savedQuery) => {
    props.onClick(savedQuery);
    props.onClose();
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setModelToDelete(false);
  };

  const handleDeleteQuery = (e) => {
    e.stopPropagation();
    if (!modelToDelete) {
      return;
    }
    setIsDeletingQuery(true);
    modelToDelete
      .delete()
      .then(() => {
        props.reload(true);
      })
      .catch((e) => {
        setIsDeletingQuery(false);
      });
  };

  const toggleConfirmDelete = (e, model) => {
    e.stopPropagation();

    // get any scheduled reports tied to this saved report
    const request = new Request("/report", [
      { field: "dataQueryId", value: model.id },
    ]);

    setIsLoadingScheduledReportsId(model.id);
    request
      .get()
      .then((scheduledReports = []) => {
        // if we find any related, scheduled reports warn the
        // user so they are aware of any inconsistencies
        if (scheduledReports.length > 0)
          setWarningText(
            `This report is attached to ${
              scheduledReports.length
            } scheduled report${
              scheduledReports.length > 1 ? "s" : ""
            }. Are you sure?`
          );
        else setWarningText("Are you sure?");

        setIsLoadingScheduledReportsId(false);
        setModelToDelete(model);
      })
      .catch((e) => {
        setIsLoadingScheduledReportsId(false);
        setModelToDelete(model);
      });
  };

  const handleEditClick = (e, model) => {
    e.stopPropagation();
    setReportModelToEdit(model);
    setEditingModelName(model.name);
  };

  const handleEditConfirm = (e) => {
    e.stopPropagation();

    try {
      reportModelToEdit.set({ name: editingModelName });
      reportModelToEdit.update();
      handleCancelEdit();
    } catch (e) {
      handleCancelEdit();
    }
  };

  const handleCancelEdit = (e) => {
    if (e) e.stopPropagation();

    setEditingModelName("");
    setReportModelToEdit({});
    setWarningText("Are you sure?");
  };

  const handleOnFocus = (e) => {
    e.stopPropagation();
  };

  const handleReportNameChange = (e) => {
    setEditingModelName(e.target.value);
  };

  return (
    <Dialog
      disableScrollLock={true}
      onClose={props.onClose}
      aria-labelledby="saved-queries-dialog-title"
      open={props.show}
      fullWidth={true}
      maxWidth="md"
      datacy={"loadSavedReportsModal"}
    >
      <DialogTitle id="saved-queries-dialog-title">Saved Reports</DialogTitle>
      <DialogContent>
        <SearchField value={savedQueryFilter} onChange={setSavedQueryFilter} />
        <List className={classes.savedQueriesList}>
          {savedQueries.length > 0 &&
            filterReports(savedQueries).map((sortedEntry) => (
              <ListItem
                key={`saved_query_${sortedEntry.model.id}`}
                button
                onClick={() => onClick(sortedEntry.model)}
                className={classes.listItem}
                datacy={"loadSavedReportsItem"}
              >
                {sortedEntry.model.id === reportModelToEdit?.id ? (
                  <div className={classes.modelNameContainer}>
                    <TextField
                      onClick={handleOnFocus}
                      value={editingModelName}
                      onChange={handleReportNameChange}
                      InputProps={{ disableUnderline: true }}
                    />
                    <div className={classes.editContainer}>
                      <CloseIcon
                        className={classes.closeIcon}
                        onClick={handleCancelEdit}
                      />
                      <CheckIcon
                        className={classes.checkIcon}
                        onClick={handleEditConfirm}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={classes.modelNameContainer}>
                    <ListItemText
                      primary={sortedEntry.model.name}
                      secondary={getSecondaryDateText(sortedEntry.model)}
                    />
                  </div>
                )}
                {isGlobal(sortedEntry.model) ? (
                  <Chip icon={<PublicIcon />} label={"Global"} /> // OR
                ) : modelToDelete &&
                  modelToDelete.id === sortedEntry.model.id ? (
                  <Fade
                    in={modelToDelete.id === sortedEntry.model.id}
                    timeout={500}
                  >
                    <div className={classes.confirmContainer}>
                      {!isDeletingQuery && <p>{warningText}</p>}
                      <Button
                        disabled={isDeletingQuery}
                        variant={"text"}
                        className={classes.confirmDelete}
                        onClick={handleDeleteQuery}
                        datacy={"deleteReportButton"}
                      >
                        {isDeletingQuery ? "Deleting..." : "Delete report"}
                      </Button>
                      {!isDeletingQuery && (
                        <Button
                          variant={"text"}
                          color={"primary"}
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </Fade> // OR
                ) : (
                  <div className={classes.savedReportBtnContainer}>
                    {isLoadingScheduledReportsId === sortedEntry.model.id ? (
                      <CircularProgress size={25} color={"primary"} />
                    ) : (
                      <span>
                        {sortedEntry.model.id !== reportModelToEdit?.id && (
                          <EditIcon
                            className={classes.editIcon}
                            color={"primary"}
                            onClick={(e) =>
                              handleEditClick(e, sortedEntry.model)
                            }
                          />
                        )}
                        <DeleteIcon
                          color={"primary"}
                          onClick={(e) =>
                            toggleConfirmDelete(e, sortedEntry.model)
                          }
                          datacy={"deleteReportIcon"}
                        />
                      </span>
                    )}
                  </div>
                )}
              </ListItem>
            ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    reload: (force) => dispatch(loadPageData(force)),
  };
};

// ** State constants *****************
const mapStateToProps = (state) => {
  return {
    license: state.license,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedQueryModal);
